import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import IdeaStatusBadge from '../components/ideas/IdeaStatusBadge';
import {
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { TwitterPreview, YouTubePreview } from '../components/publish/FormatPreview';
import PointsSection from '../components/points/PointsSection';
import toast from 'react-hot-toast';

const VALID_TRANSITIONS = {
  draft: ['developing', 'archived'],
  developing: ['ready', 'draft', 'archived'],
  ready: ['published', 'developing', 'archived'],
  published: ['archived'],
  archived: ['draft'],
};

export default function IdeaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [formatting, setFormatting] = useState(null);
  const [twitterData, setTwitterData] = useState(null);
  const [youtubeData, setYoutubeData] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleBusy, setTitleBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/ideas/${id}/`),
      api.get(`/ideas/${id}/conversations/`),
    ])
      .then(([ideaRes, convRes]) => {
        setIdea(ideaRes.data);
        setConversations(convRes.data);
      })
      .catch(() => toast.error('Failed to load idea'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleTransition = async (newStatus) => {
    try {
      const res = await api.post(`/ideas/${id}/transition/`, { status: newStatus });
      setIdea(res.data);
      toast.success(`Status changed to ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change status');
    }
  };

  const handleAssessReadiness = async () => {
    setAssessing(true);
    try {
      const res = await api.post(`/ideas/${id}/assess-readiness/`);
      setIdea((prev) => ({
        ...prev,
        ai_readiness_score: res.data.score,
        ai_readiness_reasoning: res.data.reasoning,
      }));
      toast.success(`Readiness: ${Math.round(res.data.score * 100)}%`);
    } catch (err) {
      toast.error('Failed to assess readiness');
    } finally {
      setAssessing(false);
    }
  };

  const handleFormat = async (platform) => {
    setFormatting(platform);
    try {
      const res = await api.post(`/ideas/${id}/format/${platform}/`);
      if (platform === 'twitter') setTwitterData(res.data);
      if (platform === 'youtube') setYoutubeData(res.data);
    } catch {
      toast.error(`Failed to format for ${platform}`);
    } finally {
      setFormatting(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await api.delete(`/ideas/${id}/`);
      toast.success('Idea deleted');
      navigate('/ideas');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const startEditTitle = () => {
    setTitleDraft(idea.title || '');
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    const next = titleDraft.trim();
    // Empty title isn't a save — leave it as-is rather than blanking it out.
    if (!next || next === idea.title) {
      setEditingTitle(false);
      return;
    }
    setTitleBusy(true);
    try {
      const res = await api.patch(`/ideas/${id}/`, { title: next });
      setIdea(res.data);
      setEditingTitle(false);
    } catch {
      toast.error('Failed to save title');
    } finally {
      setTitleBusy(false);
    }
  };

  const suggestTitle = async () => {
    setTitleBusy(true);
    try {
      const res = await api.post(`/ideas/${id}/auto-title/`);
      setIdea((prev) => ({ ...prev, title: res.data.title }));
      setTitleDraft(res.data.title);
      toast.success('Title suggested');
    } catch {
      toast.error('Failed to suggest a title');
    } finally {
      setTitleBusy(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle();
    } else if (e.key === 'Escape') {
      setEditingTitle(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>;
  if (!idea) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Idea not found</div>;

  const transitions = VALID_TRANSITIONS[idea.status] || [];

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <div className="mb-4">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={saveTitle}
              disabled={titleBusy}
              placeholder="Idea title"
              className="flex-1 min-w-0 text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-indigo-500 focus:outline-none disabled:opacity-50"
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={suggestTitle}
              disabled={titleBusy}
              title="Suggest a title with AI"
              className="shrink-0 p-2 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/40 rounded-lg disabled:opacity-40"
            >
              <SparklesIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={saveTitle}
              disabled={titleBusy}
              title="Save"
              className="shrink-0 p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded-lg disabled:opacity-40"
            >
              <CheckIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setEditingTitle(false)}
              disabled={titleBusy}
              title="Cancel"
              className="shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-40"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            {/* Tap the title (or the pencil) to edit it inline. */}
            <button
              type="button"
              onClick={startEditTitle}
              className="flex-1 min-w-0 text-left group"
            >
              <h1
                className={`text-2xl font-bold ${
                  idea.title
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-400 dark:text-gray-500 italic'
                }`}
              >
                {idea.title || 'Untitled — tap to name it'}
                <PencilSquareIcon className="inline-block w-5 h-5 ml-2 -mt-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h1>
            </button>
            {!idea.title && (
              <button
                type="button"
                onClick={suggestTitle}
                disabled={titleBusy}
                className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 rounded-lg disabled:opacity-40"
              >
                <SparklesIcon className="w-4 h-4" />
                {titleBusy ? 'Thinking…' : 'Suggest'}
              </button>
            )}
          </div>
        )}
        <div className="mt-2 flex items-center gap-3">
          <IdeaStatusBadge status={idea.status} />
          {idea.ai_readiness_score != null && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Readiness: {Math.round(idea.ai_readiness_score * 100)}%
            </span>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6">
        <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Raw Input</h2>
        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{idea.raw_input}</p>
      </div>

      {idea.summary && (
        <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Summary</h2>
          <p className="text-gray-900 dark:text-white">{idea.summary}</p>
        </div>
      )}

      {/* Points are the substance of the idea, so they sit above the meta. */}
      <div className="mb-6">
        <PointsSection ideaId={id} />
      </div>

      {idea.ai_readiness_reasoning && (
        <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">AI Assessment</h2>
          <div className="mb-2">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full"
                style={{ width: `${(idea.ai_readiness_score || 0) * 100}%` }}
              />
            </div>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">{idea.ai_readiness_reasoning}</p>
        </div>
      )}

      {idea.tags?.length > 0 && (
        <div className="flex gap-2 mb-6 flex-wrap">
          {idea.tags.map((tag) => (
            <span key={tag} className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Status transitions */}
      {transitions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Move to</h2>
          <div className="flex gap-2 flex-wrap">
            {transitions.map((s) => (
              <button
                key={s}
                onClick={() => handleTransition(s)}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 capitalize transition-colors duration-200"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-8 flex-wrap">
        <button
          onClick={() => navigate(`/ideas/${id}/chat`)}
          className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
        >
          Develop Idea
        </button>
        <button
          onClick={handleAssessReadiness}
          disabled={assessing}
          className="px-4 py-2 bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 border border-indigo-600 dark:border-indigo-400 rounded-lg text-sm font-medium hover:bg-indigo-50 dark:hover:bg-indigo-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {assessing ? 'Assessing...' : 'Assess Readiness'}
        </button>
        <button
          onClick={handleDelete}
          className="px-4 py-2 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900 transition-colors duration-200"
        >
          Delete
        </button>
      </div>

      {/* Publish / Format */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Format for Publishing</h2>
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => handleFormat('twitter')}
            disabled={formatting === 'twitter'}
            className="px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-medium hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {formatting === 'twitter' ? 'Formatting...' : 'Format for X/Twitter'}
          </button>
          <button
            onClick={() => handleFormat('youtube')}
            disabled={formatting === 'youtube'}
            className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {formatting === 'youtube' ? 'Formatting...' : 'YouTube Script'}
          </button>
        </div>
        {twitterData && <TwitterPreview data={twitterData} />}
        {youtubeData && <div className="mt-4"><YouTubePreview data={youtubeData} /></div>}
      </div>

      {conversations.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Conversations</h2>
          <div className="space-y-2">
            {conversations.map((conv) => (
              <Link
                key={conv.id}
                to={`/ideas/${id}/chat/${conv.id}`}
                className="flex items-center gap-3 bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow"
              >
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{conv.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {conv.message_count} messages · {conv.persona}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
